// 설비 자동 데이터 수집 (POP)
import { useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Card } from '../components/Card'
import { Button } from '../components/Button'
import { SkeletonRows } from '../components/Skeleton'
import { api } from '../lib/api'
import { useToast } from '../components/Toast'
import { useGlobalReveal } from '../hooks/useReveal'
import { useAuth, hasPerm } from '../lib/auth'
import { useT } from '../lib/i18n'

interface Eq {
  equipment_id: string
  equipment_name: string
  equipment_type: string
  protocol: string
}

export function EquipmentDataPage() {
  useGlobalReveal()
  const toast = useToast()
  const { t } = useT()
  const { user } = useAuth()
  const canWrite = hasPerm(user, 'equipment_data', 'write')
  const [eqs, setEqs] = useState<Eq[] | null>(null)
  const [selected, setSelected] = useState<string>('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    api.get<Eq[]>('/equipment').then((d) => { setEqs(d); if (d[0]) setSelected(d[0].equipment_id) }).catch(() => setEqs([]))
  }, [])

  const send = async (status: 'Running' | 'Idle' | 'Alarm' | 'Down') => {
    if (!selected) return
    setBusy(true)
    try {
      await api.post('/equipment/data', {
        equipment_id: selected,
        production_count: Math.floor(100 + Math.random() * 400),
        operating_status: status,
        alarm_code: status === 'Alarm' ? 'A001' : undefined,
        cycle_time: Number((20 + Math.random() * 20).toFixed(2)),
      })
      toast.push('success', t('eqd.sent', { eq: selected, status }))
    } catch (e: any) {
      toast.push('error', e?.message ?? t('toast.actionFailed'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <main id="main" className="container stack" style={{ paddingBlock: '2rem' }}>
      <h1 className="page-title reveal">
        <FontAwesomeIcon icon="microchip" /> {t('eqd.title')}
      </h1>
      <p className="muted reveal">{t('eqd.intro')}</p>

      <Card glass tilt={false} spotlight={false} className="reveal" as="section" aria-labelledby="send-h">
        <h2 id="send-h" style={{ fontSize: 'var(--fs-lg)' }}>{t('eqd.panel')}</h2>
        <div className="row">
          <div className="field" style={{ minWidth: 260, marginBottom: 0 }}>
            <label htmlFor="eq-sel">{t('eqd.selectEq')}</label>
            <select id="eq-sel" value={selected} onChange={(e) => setSelected(e.target.value)} disabled={!eqs}>
              {!eqs && <option>{t('common.loading')}</option>}
              {eqs?.map((e) => (
                <option key={e.equipment_id} value={e.equipment_id}>
                  {e.equipment_id} — {e.equipment_name} ({e.protocol})
                </option>
              ))}
            </select>
          </div>
          {(['Running','Idle','Alarm','Down'] as const).map((s) => (
            <Button
              key={s}
              variant={s === 'Alarm' ? 'danger' : s === 'Running' ? 'accent' : 'ghost'}
              onClick={() => send(s)}
              disabled={!canWrite || busy || !selected}
            >
              <FontAwesomeIcon icon={s === 'Running' ? 'play' : s === 'Alarm' ? 'triangle-exclamation' : 'pause'} />
              <span>{t('eqd.send', { status: s })}</span>
            </Button>
          ))}
        </div>
      </Card>

      <Card glass tilt={false} spotlight={false} className="reveal">
        <h2 style={{ fontSize: 'var(--fs-lg)' }}>{t('eqd.registered')}</h2>
        {!eqs ? (
          <SkeletonRows rows={3} />
        ) : eqs.length === 0 ? (
          <div className="empty-state" role="status">
            <FontAwesomeIcon icon="microchip" className="icon-lg" />
            <p className="muted" style={{ margin: 0 }}>{t('common.empty')}</p>
          </div>
        ) : (
          <div className="table-wrap" style={{ '--table-min-w': '560px' } as React.CSSProperties}>
            <table className="table" aria-label={t('eqd.aria.list')}>
              <thead>
                <tr><th>{t('eqd.col.id')}</th><th>{t('eqd.col.name')}</th><th>{t('eqd.col.type')}</th><th>{t('eqd.col.protocol')}</th></tr>
              </thead>
              <tbody>
                {eqs.map((e) => (
                  <tr key={e.equipment_id}>
                    <td className="mono">{e.equipment_id}</td>
                    <td>{e.equipment_name}</td>
                    <td>{e.equipment_type}</td>
                    <td><span className="badge info">{e.protocol}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </main>
  )
}
